/**
 * @fileoverview User-facing formatting of the web app build version. Used by
 * tenant-web, system-web, and mobile-pwa wherever the version is shown in the UI.
 * @module @firesquirrel/web-config-client
 *
 * Responsibilities:
 * - Turn the build version (from VITE_BUILD_VERSION) into the string shown to users:
 *   - current `YYYY.MM.DD.SSSSS-<sha7>` (same on dev, stage and prod) → `YYYY.MM.DD.SSSSS`
 *   - legacy `YYYY.MM.DD.N-prod` → `YYYY.MM.DD.N`; legacy dev/stage versions unchanged.
 *
 * Dependencies:
 * - None (pure function).
 *
 * Architecture:
 * - Works from the version string alone, so callers do not need to know which
 *   environment they run in. The current format carries no environment: the
 *   same code shows the same version in every environment.
 *
 * Notes:
 * - Display only. Telemetry, device records, and update checks must keep the
 *   full version: the short SHA identifies the exact commit.
 * - Unknown formats are returned unchanged rather than guessed at.
 */

/** Shown when no build version is available (local `npm run dev`, PR builds). */
export const UNVERSIONED_BUILD_LABEL = 'dev';

/** Current format: `YYYY.MM.DD.SSSSS-<sha7>` (UTC date, seconds into the day, short commit SHA). */
const CODE_VERSION_RE = /^(\d{4}\.\d{2}\.\d{2}\.\d{5})-[0-9a-f]{7}$/;

/** Legacy counter format, kept so older builds still display sensibly. */
const PROD_VERSION_RE = /^(\d{4}\.\d{2}\.\d{2}\.\d+)-prod$/;

/**
 * Format a build version for display.
 *
 * @contract
 * - `undefined` / empty → `'dev'`
 * - `YYYY.MM.DD.SSSSS-<sha7>` → `YYYY.MM.DD.SSSSS` (the SHA stays in the full version / version.json)
 * - `YYYY.MM.DD.N-prod` (legacy) → `YYYY.MM.DD.N`
 * - anything else (e.g. `2026.10.07.3-stage`) → unchanged
 *
 * @example formatDisplayVersion(import.meta.env.VITE_BUILD_VERSION) // "2026.10.07.3"
 */
export function formatDisplayVersion(version: string | undefined | null): string {
  const trimmed = version?.trim();
  if (!trimmed) {
    return UNVERSIONED_BUILD_LABEL;
  }
  const code = CODE_VERSION_RE.exec(trimmed);
  if (code) {
    return code[1];
  }
  const prod = PROD_VERSION_RE.exec(trimmed);
  return prod ? prod[1] : trimmed;
}
